import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Spinner, Button, Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import './SearchPage.css';

// Import IntegratedSearchNavBar instead of SearchForm
import IntegratedSearchNavBar from '../navigation/IntegratedSearchNavBar';
import SearchResultCard from '../search/SearchResultCard';
import useCollections, { CollectionModal } from '../../hooks/useCollections';
import AIContentDisplay from '../search/AIContentDisplay';
import {
  useHandleContainerClickRecents,
  useHandleContainerClickRecentsCanada,
  useHandleContainerClickRecentsMexico
} from '../../hooks/useHandleContainerClicks';

import {
  runSearch,
  formatDate,
  getCountryDetails
} from '../search/searchUtils';

// AI Search Limiting
import { canUseAISearch, recordAISearch, getRemainingSearches } from '../../utils/aiSearchLimit';

import RelevantPagesSection from './RelevantPagesSection';
const apiKey = process.env.REACT_APP_API_KEY;
const SearchPage = () => {
  const { t } = useTranslation('common');
  const { country: initialCountry } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const handleContainerClickUS = useHandleContainerClickRecents();
  const handleContainerClickCanada = useHandleContainerClickRecentsCanada();
  const handleContainerClickMexico = useHandleContainerClickRecentsMexico();

  // Use the collection hook for collection functionality
  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    openCollectionModal,
    closeModal,
    handleSave,
    isStorageAvailable
  } = useCollections();

  // Enhanced country details function with all countries
  const getEnhancedCountryDetails = useCallback((countrySchema) => {
    const countryMap = {
      // Special cases
      'all': { name: 'All Countries', emoji: '🌐', link: '/', apiCode: 'ALL', legacyCode: '' },
      '': { name: 'All Countries', emoji: '🌐', link: '/', apiCode: 'ALL', legacyCode: '' },
      
      // Legacy countries (with both legacy and main API support)
      'us': { name: 'United States', emoji: '🇺🇸', link: '/us', apiCode: 'US', legacyCode: 'united_states_of_america' },
      'canada': { name: 'Canada', emoji: '🇨🇦', link: '/canada', apiCode: 'CA', legacyCode: 'canada' },
      'mexico': { name: 'Mexico', emoji: '🇲🇽', link: '/mexico', apiCode: 'MX', legacyCode: 'mexico' },
      'uk': { name: 'United Kingdom', emoji: '🇬🇧', link: '/uk', apiCode: 'UK', legacyCode: 'united_kingdom' },
      'united_kingdom': { name: 'United Kingdom', emoji: '🇬🇧', link: '/uk', apiCode: 'UK', legacyCode: 'united_kingdom' },
      'united_states_of_america': { name: 'United States', emoji: '🇺🇸', link: '/us', apiCode: 'US', legacyCode: 'united_states_of_america' },
      
      // New countries (main API only)
      'australia': { name: 'Australia', emoji: '🇦🇺', link: '/australia', apiCode: 'AU', legacyCode: '' },
      'france': { name: 'France', emoji: '🇫🇷', link: '/france', apiCode: 'FR', legacyCode: '' },
      'italy': { name: 'Italy', emoji: '🇮🇹', link: '/italy', apiCode: 'IT', legacyCode: '' },
      'japan': { name: 'Japan', emoji: '🇯🇵', link: '/japan', apiCode: 'JP', legacyCode: '' },
      'south-africa': { name: 'South Africa', emoji: '🇿🇦', link: '/south-africa', apiCode: 'ZA', legacyCode: '' },
      'ukraine': { name: 'Ukraine', emoji: '🇺🇦', link: '/ukraine', apiCode: 'UA', legacyCode: '' }
    };
    return countryMap[countrySchema] || { name: 'Unknown', emoji: '❓', link: '/', apiCode: 'ALL', legacyCode: '' };
  }, []);

  // Map URL country params to schema values for consistency
  const mapCountryToSchema = useCallback((countryParam) => {
    const countryMap = {
      // Legacy countries
      'canada': 'canada',
      'mexico': 'mexico', 
      'uk': 'united_kingdom',
      'united_kingdom': 'united_kingdom',
      'us': 'united_states_of_america',
      'united_states_of_america': 'united_states_of_america',
      
      // New countries
      'australia': 'australia',
      'china': 'china',
      'france': 'france',
      'germany': 'germany',
      'israel': 'israel',
      'italy': 'italy',
      'japan': 'japan',
      'nigeria': 'nigeria',
      
      'south-africa': 'south-africa',
      'ukraine': 'ukraine',
      
      // Special cases
      'all': ''
    };
    return countryMap[countryParam] || countryParam || '';
  }, []);

  // Map schema values back to URL-friendly country codes
  const mapSchemaToCountry = useCallback((schema) => {
    const schemaMap = {
      // Legacy countries
      'canada': 'canada',
      'mexico': 'mexico',
      'united_kingdom': 'uk', 
      'united_states_of_america': 'us',
      
      // New countries
      'australia': 'australia',
      
      'china': 'china',
      'france': 'france',
      'germany': 'germany',
      'israel': 'israel',
      'italy': 'italy',
      'japan': 'japan',
      'nigeria': 'nigeria',
      
      'south-africa': 'south-africa',
      'ukraine': 'ukraine',
      
      // Special cases
      '': 'all'
    };
    return schemaMap[schema] || 'all';
  }, []);

  // Check if country should use legacy API (only the original 4 countries)
  const shouldUseLegacyAPI = useCallback((countryCode) => {
    const countryDetails = getEnhancedCountryDetails(countryCode);
    const legacyCountries = [
      'us', 'canada', 'mexico', 'uk', 'united_kingdom', 'united_states_of_america'
    ];
    return legacyCountries.includes(countryCode) && countryDetails.legacyCode !== '';
  }, [getEnhancedCountryDetails]);

  const [country, setCountry] = useState(() => mapCountryToSchema(initialCountry));
  const [inputText, setInputText] = useState('');
  const [typedText, setTypedText] = useState('');
  const [data, setData] = useState(null);
  const [legacyData, setLegacyData] = useState(null);
  const [searchResponse, setSearchResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [legacyLoading, setLegacyLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedLength, setFeedLength] = useState(10);

  // Tracks the last country+query we actually fetched, so the URL-driven
  // effect below fetches exactly once per distinct search and never re-fires
  // for a query that's already loaded.
  const lastFetchedRef = useRef('');

  // Compute AI limit status directly (no state needed)
  const isOverAILimit = !canUseAISearch();

  // URL is the source of truth. Fetch whenever there's a query we haven't
  // already fetched (deduped by country+query). This shows results on type,
  // on direct load, and on back/forward, without re-firing for a query that's
  // already loaded.
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const query = queryParams.get('query') || '';
    const urlCountry = mapCountryToSchema(initialCountry);

    setInputText(query);
    setCountry(urlCountry);

    if (!query) {
      lastFetchedRef.current = '';
      return;
    }

    const fetchKey = `${urlCountry}::${query}`;
    if (lastFetchedRef.current !== fetchKey) {
      lastFetchedRef.current = fetchKey;
      searchItems(100, query, urlCountry);
    }
  }, [location, initialCountry, mapCountryToSchema]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset search data when country changes without existing search
  useEffect(() => {
    if (!inputText.trim()) {
      setData(null);
      setLegacyData(null);
      setSearchResponse(null);
      setError(null);
    }
  }, [country]);

  // Legacy API search function (only for legacy countries)
  const runLegacySearch = useCallback(async (query, searchCountry) => {
    if (!query || query.trim() === '') {
      return;
    }

    const countryDetails = getEnhancedCountryDetails(searchCountry);
    
    if (!countryDetails.legacyCode || countryDetails.legacyCode === '') {
      return;
    }

    setLegacyLoading(true);
    const url = `https://api.govglance.org/search/?limit=20&skip=0&schema=${countryDetails.legacyCode}&search_term=${encodeURIComponent(query)}`;

    try {
      const response = await fetch(url, { 
        method: 'GET', 
        headers: { 
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + process.env.REACT_APP_API_KEY,
        } 
      });
      
      if (response.status === 404) {
        setLegacyData([]);
        return;
      }
      
      if (!response.ok) {
        throw new Error(`Legacy API error! status: ${response.status}`);
      }

      const responseData = await response.json();
      setLegacyData(responseData || []);
      
    } catch (error) {
      console.error('Legacy API error:', error);
      setLegacyData([]);
    } finally {
      setLegacyLoading(false);
    }
  }, [getEnhancedCountryDetails]);

  // Main search function with dual API support and AI limiting
  const searchItems = async (limit, query, searchCountry = country) => {
    if (!query || !query.trim()) {
      setError('');
      setData(null);
      setLegacyData(null);
      setSearchResponse(null);
      return;
    }

    setLoading(true);
    setError(null);
    
    const canUseAI = canUseAISearch();
    
    try {
      const countryDetails = getEnhancedCountryDetails(searchCountry);

      // Run legacy search in parallel if needed
      if (shouldUseLegacyAPI(searchCountry)) {
        runLegacySearch(query, searchCountry);
      } else {
        setLegacyData(null);
      }

      // Only call main AI search API if under limit
      if (canUseAI) {
        let url = `https://search.govglance.org/api/public-search?query=${encodeURIComponent(query)}`;
        if (countryDetails.apiCode && countryDetails.apiCode !== 'ALL') {
          url += `&country=${countryDetails.apiCode}`;
        }
        url += '&format=json';

        const response = await fetch(url, { 
          method: 'GET', 
          headers: { 'Content-Type': 'application/json' } 
        });
        
        if (response.status === 404) {
          throw new Error('No results found');
        }
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        setSearchResponse(responseData);
        
        recordAISearch();
        
        if (responseData.citations && responseData.citations.length > 0) {
          setData(responseData.citations);
        } else {
          setData([]);
        }
      }
      
    } catch (err) {
      console.error('Main API error:', err);
      setError(err.message);
      setData(null);
      setSearchResponse(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle search from IntegratedSearchNavBar
  const handleIntegratedSearch = useCallback((query) => {
    const trimmedQuery = query.trim();
    setInputText(trimmedQuery);
    
    if (!trimmedQuery) {
      setData(null);
      setLegacyData(null);
      setSearchResponse(null);
      setError(null);
      // Update URL to remove query
      const urlCountry = mapSchemaToCountry(country);
      const newUrl = urlCountry !== 'all' 
        ? `/search/${urlCountry}` 
        : '/search';
      navigate(newUrl, { replace: true });
      return;
    }

    setFeedLength(10);
    
    // Update URL with search query — this triggers the location useEffect → searchItems
    const urlCountry = mapSchemaToCountry(country);
    const newUrl = urlCountry !== 'all'
      ? `/search/${urlCountry}?query=${encodeURIComponent(trimmedQuery)}&limit=10`
      : `/search?query=${encodeURIComponent(trimmedQuery)}&limit=10`;
    navigate(newUrl, { replace: true });
  }, [country, navigate, mapSchemaToCountry]);

  // Handle country change from navigation dropdown
  const handleCountryChange = useCallback((newCountryCode) => {
    const countrySchemaMap = {
      // Legacy countries
      'us': 'united_states_of_america',
      'canada': 'canada', 
      'mexico': 'mexico',
      'uk': 'united_kingdom',
      
      // New countries
      'australia': 'australia',
      
      'china': 'china',
      'france': 'france',
      'germany': 'germany',
      'israel': 'israel',
      'italy': 'italy',
      'japan': 'japan',
      'nigeria': 'nigeria',
      
      'south-africa': 'south-africa',
      'ukraine': 'ukraine',
      
      // Special case
      'all': ''
    };
    
    const newSchema = countrySchemaMap[newCountryCode] || '';
    setCountry(newSchema);
    setData(null);
    setLegacyData(null);
    setSearchResponse(null);
    setLoading(false);
    setLegacyLoading(false);
    setError(null);
    setFeedLength(10);
    
    // Update URL. The location effect re-fetches once for the new
    // country+query combination (deduped via lastFetchedRef).
    if (inputText.trim()) {
      const newUrl = newCountryCode !== 'all'
        ? `/search/${newCountryCode}?query=${encodeURIComponent(inputText)}&limit=10`
        : `/search?query=${encodeURIComponent(inputText)}&limit=10`;
      navigate(newUrl, { replace: true });
    } else {
      const newUrl = newCountryCode !== 'all' 
        ? `/search/${newCountryCode}` 
        : '/search';
      navigate(newUrl, { replace: true });
    }
  }, [inputText, navigate]);

  const handleLoadMore = useCallback(() => {
    const newLimit = feedLength + 20;
    setFeedLength(newLimit);
    searchItems(newLimit, inputText, country);
  }, [feedLength, inputText, country]);

  // Handle saving to collection - prepare item for the hook
  const SaveFeedItem = useCallback((item) => {
    if (!item) return;
    
    const countryDetails = getEnhancedCountryDetails(country);
    
    const collectionItem = {
      title: item.title || item.status_title || '',
      url: item.detailsLink || item.url || '',
      docLink: item.docLink || item.document_link || '',
      date: item.created_at || item.date || new Date().toISOString(),
      feedName: item.feedName || `${countryDetails.name} Search`,
      branch: item.branchName || '',
      topic: item.topicName || ''
    };
    
    openCollectionModal(collectionItem);
  }, [country, getEnhancedCountryDetails, openCollectionModal]);

  const selectedHandleContainerClick = (() => {
    switch (country) {
      case 'canada': return handleContainerClickCanada;
      case 'mexico': return handleContainerClickMexico;
      default: return handleContainerClickUS;
    }
  })();

  // Get current country code for IntegratedSearchNavBar
  const currentCountryCode = mapSchemaToCountry(country);

  // Live typed value drives suggested pages; committed query (inputText) drives results.
  const trimmedTyped = typedText.trim();
  const isTyping = trimmedTyped !== '' && trimmedTyped !== inputText.trim();
  const suggestionQuery = trimmedTyped !== '' ? typedText : inputText;

  return (
    <>
      <IntegratedSearchNavBar
        searchQuery={inputText}
        onSearchChange={handleIntegratedSearch}
        onInputChange={setTypedText}
        currentCountry={currentCountryCode}
        placeholder={t('common:searchGovGlance', 'Search Gov Glance')}
        onCountryChange={handleCountryChange}
      />

      <div id="homebg" className="search-page-root">
        <br /><br />

        {/* Suggested pages: shown live as the user types */}
        {/* {suggestionQuery.trim() && (
          <RelevantPagesSection 
            searchQuery={suggestionQuery} 
            country={currentCountryCode} 
          />
        )} */}

        {/* Everything below is hidden while the user is actively typing */}
        {!isTyping && (
          <>
            {/* Loading indicators */}
            {(loading || legacyLoading) && (
              <div className="d-flex justify-content-center align-items-center" style={{ padding: '2rem 0' }}>
                <div className="text-center">
                  <Spinner animation="grow" variant="light" />
                  <div className="mt-2 search-loading-text">
                    {loading && legacyLoading
                      ? t('common:searchingDatabases', 'Searching databases...')
                      : t('common:searching', 'Searching...')}
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div id="billText" className="error-message text-center p-3">
                {error === 'No results found'
                  ? t('common:noResultsFor', 'No results found for "{{query}}"', { query: inputText })
                  : error}
              </div>
            )}

            {/* AI Search Limit Reached Message */}
            {isOverAILimit && (
              <div className="ai-limit-message" style={{ textAlign: 'center', margin: '1rem auto' }}>
                <h5 className="ai-limit-title">
                  {t('common:aiSearchLimitTitle', 'AI Search Limit Reached')}
                </h5>
                <p className="ai-limit-text">
                  {t('common:aiSearchLimitMessage', "You've used your 20 free AI-powered searches this month.")}
                </p>
                <p className="ai-limit-subtext">
                  {t('common:aiSearchLimitSubtext', 'Regular search results are still shown below.')}
                </p>
                <a 
                  href="https://search.govglance.org" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="ai-limit-button"
                >
                  {t('common:getMoreAISearches', 'Get More AI Searches →')}
                </a>
              </div>
            )}

            {/* Display AI-generated content if available */}
            <AIContentDisplay 
              searchResponse={searchResponse}
              onCitationClick={(citationNumber) => {}}
            />

            {/* Display main API citations */}
            {data && data.length > 0 && (
              <div>
                {searchResponse?.content && (
                <h4 className="search-sources-heading text-center">
                  {t('common:sourcesHeading', 'Sources')}
                </h4>
                )}
                {data.map((item, index) => (
                  <SearchResultCard
                    key={item.url || `main-${index}`}
                    item={item}
                    country={country}
                    onSave={SaveFeedItem}
                    onContainerClick={selectedHandleContainerClick}
                    index={index}
                    showCitations={searchResponse?.content ? true : false}
                    isLegacyResult={false}
                  />
                ))}
              </div>
            )}

            {/* Display legacy API results (only for legacy countries) */}
            {legacyData && legacyData.length > 0 && (
              <div>
                <div className="text-center mb-3 search-database-text">
                  <small>
                    {isOverAILimit 
                      ? t('common:dbResultsFromGov', 'Search results from government databases')
                      : t('common:dbResultsAdditional', 'Additional results from government databases')
                    }
                  </small>
                </div>
                {legacyData.map((item, index) => (
                  <SearchResultCard
                    key={`legacy-${item.id}-${index}`}
                    item={item}
                    country={country}
                    onSave={SaveFeedItem}
                    onContainerClick={selectedHandleContainerClick}
                    index={index}
                    showCitations={false}
                    isLegacyResult={true}
                  />
                ))}
                
                <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                  <Button 
                    id='searchViewMoreButtonMobile' 
                    onClick={handleLoadMore}
                    disabled={loading || legacyLoading}
                    variant="outline-light"
                    size="lg"
                  >
                    {(loading || legacyLoading) ? <Spinner animation="grow" variant="light"  /> : t('common:loadMore', 'Load More')}
                  </Button>
                </div>
              </div>
            )}

            {/* Show message if legacy API failed but main API worked (only for legacy countries) */}
            {data && data.length > 0 && shouldUseLegacyAPI(currentCountryCode) && 
             (!legacyData || legacyData.length === 0) && !legacyLoading && (
              <div className="text-center p-3 search-note-text">
                <small>
                  {t('common:dbResultsUnavailable', 'Note: Additional government database results temporarily unavailable')}
                </small>
              </div>
            )}

            {/* No results message */}
            {((data && data.length === 0) || (!data)) && 
             ((legacyData && legacyData.length === 0) || (!legacyData)) && 
             !loading && !legacyLoading && inputText.trim() && !isOverAILimit && (
              <div className="text-center p-5 search-no-results">
                <h4>{t('common:noResultsFound', 'No results found')}</h4>
                <p>{t('common:tryDifferentSearch', 'Try different search terms or adjust your country selection')}</p>
              </div>
            )}
          </>
        )}

        {/* Collection Modal - from useCollections hook */}
        <CollectionModal
          isModalOpen={isModalOpen}
          currentItem={currentItem}
          saveSuccess={saveSuccess}
          note={note}
          setNote={setNote}
          handleSave={handleSave}
          closeModal={closeModal}
          isStorageAvailable={isStorageAvailable}
        />
      </div>
    </>
  );
};

export default SearchPage;